import { Stack } from '@mui/material';
import { useMemo } from 'react';

import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.constants';
import { AIOnboardingModalFormShape } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import { SuggestionNotesHelp } from '@/modules/aiSuggestions/components/SuggestionNotesHelp';
import { testamentaryEntityKindToDisplayName } from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { EntityInEstateStatus, TestamentaryEntityKind } from '@/types/schema';
import { PathsOf } from '@/types/subform';

interface TestamentaryEntityFieldsRowProps {
  index: number;
  suggestionID: string | undefined;
}

export function TestamentaryEntityFieldsRow({
  index,
  suggestionID,
}: TestamentaryEntityFieldsRowProps) {
  const { control } = useFormContext<AIOnboardingModalFormShape>();
  const { suggestionsByID } = useAIOnboardingModalContext();

  const suggestionNotes = suggestionID
    ? suggestionsByID[suggestionID]?.onboardingTestamentaryEntity?.notes
    : undefined;

  const testamentaryEntityKindOptions = useMemo(() => {
    return Object.values(TestamentaryEntityKind).map((kind) => ({
      value: kind as TestamentaryEntityKind,
      display: testamentaryEntityKindToDisplayName(
        kind as TestamentaryEntityKind
      ),
    }));
  }, []);

  const testamentaryEntityOutOfEstateOptions = useMemo(() => {
    return [
      { value: EntityInEstateStatus.InEstate, display: 'In Estate' },
      { value: EntityInEstateStatus.OutOfEstate, display: 'Out of Estate' },
    ];
  }, []);

  const { styles, columns } =
    AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS.testamentaryEntities;

  return (
    <Stack spacing={1}>
      <FormLayoutRow
        // !important is required to beat FormLayoutRow's styled `:not(:last-child)` margin-bottom rule, which has higher specificity than sx.
        sx={{ mb: `0 !important`, gap: styles.columnPaddingHorizontal }}
      >
        <FormLayoutItem width={columns.testamentaryEntityKind.columnWidth}>
          <FormAwareSelectInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `testamentaryEntities.${index}.testamentaryEntityKind` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.testamentaryEntityKind.columnHeader}
            options={testamentaryEntityKindOptions}
            hideLabel
            required
            showErrorBeforeSubmit
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.testamentaryEntityName.columnWidth}>
          <FormAwareTextInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `testamentaryEntities.${index}.testamentaryEntityName` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.testamentaryEntityName.columnHeader}
            endAdornment={
              suggestionNotes ? (
                <SuggestionNotesHelp notes={suggestionNotes} />
              ) : undefined
            }
            hideLabel
            required
            showErrorBeforeSubmit
          />
        </FormLayoutItem>
        <FormLayoutItem
          width={columns.testamentaryEntityAssetLocation.columnWidth}
        >
          <FormAwareSelectInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `testamentaryEntities.${index}.testamentaryEntityAssetLocation` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.testamentaryEntityAssetLocation.columnHeader}
            options={testamentaryEntityOutOfEstateOptions}
            hideLabel
            required
            showErrorBeforeSubmit
          />
        </FormLayoutItem>
      </FormLayoutRow>
    </Stack>
  );
}
